/**
 * HIPInfoCard - Draggable Hurricane Info Card
 *
 * Displays storm info summary in a draggable card using AbstractInset.
 */

import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import AbstractInset from "./AbstractInset";

interface HIPInfoCardProps {
  stormName?: string;
  stormExternalId?: string;
  category: number;
  maxWindSpeed: number;
  selectedModel: string;
  intensity: number;
  trackShift: number;
  districtsAffected: number;
  totalOutages: number;
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "middle-right";
  width?: string;
  initiallyCollapsed?: boolean;
  isCollapsible?: boolean;
  className?: string;
  panelMode?: boolean;
}

function getCategoryColor(category: number): string {
  if (category >= 5) return "#7c2d12";
  if (category >= 4) return "#dc2626";
  if (category >= 3) return "#f97316";
  if (category >= 2) return "#eab308";
  if (category >= 1) return "#22c55e";
  return "#60a5fa"; // Tropical Storm
}

export function HIPInfoCard({
  stormName,
  stormExternalId,
  category,
  maxWindSpeed,
  selectedModel,
  intensity,
  trackShift,
  districtsAffected,
  totalOutages,
  position = "middle-right",
  width = "300px",
  initiallyCollapsed = false,
  isCollapsible = true,
  className,
  panelMode = false,
}: HIPInfoCardProps) {
  const title = (
    <span className="flex items-center gap-2">
      <AlertTriangle className="h-4 w-4" />
      {stormName || "No Storm Selected"}
      {category > 0 && (
        <Badge
          style={{ backgroundColor: getCategoryColor(category) }}
          className="text-white ml-2"
        >
          Cat {category}
        </Badge>
      )}
    </span>
  );

  return (
    <AbstractInset
      containerId="hip-info-card"
      data={[]}
      position={position}
      width={width}
      height="auto"
      title={stormName || "Storm Info"}
      subtitle={stormExternalId || "Select a storm to view details"}
      isCollapsible={isCollapsible}
      initiallyCollapsed={initiallyCollapsed}
      isDraggable={true}
      className={className}
      panelMode={panelMode}
    >
      <div className="p-3">
        {/* Custom header with category badge */}
        {category > 0 && (
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Hurricane Category</span>
            <Badge
              style={{ backgroundColor: getCategoryColor(category) }}
              className="text-white"
            >
              Category {category}
            </Badge>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Max Wind</p>
            <p className="text-lg font-bold">
              {maxWindSpeed > 0 ? `${Math.round(maxWindSpeed)} mph` : "-"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Model</p>
            <p className="text-lg font-bold">{selectedModel || "-"}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Intensity</p>
            <p className="text-lg font-bold">
              {intensity === 100
                ? "Normal"
                : intensity > 100
                ? `+${intensity - 100}%`
                : `-${100 - intensity}%`}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Track Shift</p>
            <p className="text-lg font-bold">
              {trackShift === 0
                ? "None"
                : trackShift > 0
                ? `${trackShift}mi NE`
                : `${Math.abs(trackShift)}mi SW`}
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t">
          <p className="text-xs text-muted-foreground font-semibold mb-2">Impact Summary</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span>Districts Affected:</span>
              <span className="font-medium">{districtsAffected}</span>
            </div>
            <div className="flex justify-between">
              <span>Estimated Outages:</span>
              <span className="font-medium text-red-500">
                {Math.round(totalOutages).toLocaleString()} customers
              </span>
            </div>
          </div>
        </div>
      </div>
    </AbstractInset>
  );
}

export default HIPInfoCard;
